/**
 * hooks/useDocumentConversion.ts
 *
 * Handles PDF conversion retry logic for the ClientDocumentsTab.
 *
 * Exposes:
 * - retryConversion: retry a single failed document (shows toast on completion)
 * - retryAllFailed: retry all failed documents in one go (shows toast on completion)
 * - retryingDocs: Set of document IDs currently being retried
 *
 * Created Mar 2026: PDF conversion and intelligent renaming feature.
 * Updated Mar 2026: callConvertDocuments now checks resp.ok and throws a descriptive
 *   Error for non-2xx responses so callers' try/catch surfaces server/auth errors.
 * Updated Apr 2026: Removed downloadAllAsZip, isZipping, zipProgress (dead exports,
 *   ZIP download feature is not used).
 */

import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import type { DocumentEntry } from '../types/dossier';

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/convert-documents`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function callConvertDocuments(
  requestId: string,
  documentIds: string[],
  accessToken: string,
): Promise<{ converted: number; failed: number; errors: string[] }> {
  const resp = await fetch(EDGE_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'Apikey': ANON_KEY,
    },
    body: JSON.stringify({ request_id: requestId, document_ids: documentIds }),
  });
  if (!resp.ok) {
    const bodyText = await resp.text();
    throw new Error(`Conversion request failed (${resp.status}): ${bodyText}`);
  }
  return resp.json();
}

export function useDocumentConversion(onRefresh: () => void) {
  const { showToast } = useToast();
  const [retryingDocs, setRetryingDocs] = useState<Set<string>>(new Set());

  const retryConversion = async (docId: string, requestId: string) => {
    setRetryingDocs((prev) => new Set([...prev, docId]));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const result = await callConvertDocuments(requestId, [docId], session.access_token);
      if (result.converted > 0) {
        showToast('Document converti avec succès', 'success');
        onRefresh();
      } else {
        showToast(`Échec : ${result.errors?.[0] ?? 'Erreur inconnue'}`, 'error');
        onRefresh();
      }
    } catch {
      showToast('Erreur lors de la conversion', 'error');
    } finally {
      setRetryingDocs((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  const retryAllFailed = async (failedDocs: DocumentEntry[]) => {
    if (failedDocs.length === 0) return;
    const byRequest = new Map<string, string[]>();
    failedDocs.forEach((doc) => {
      const arr = byRequest.get(doc.requestId) ?? [];
      arr.push(doc.id);
      byRequest.set(doc.requestId, arr);
    });
    setRetryingDocs(new Set(failedDocs.map((d) => d.id)));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      let totalConverted = 0;
      for (const [requestId, docIds] of byRequest.entries()) {
        const result = await callConvertDocuments(requestId, docIds, session.access_token);
        totalConverted += result.converted ?? 0;
      }
      if (totalConverted > 0) {
        const msg = totalConverted === 1
          ? '1 document converti avec succès'
          : `${totalConverted} documents convertis avec succès`;
        showToast(msg, 'success');
      } else {
        showToast("Aucun document n'a pu être converti", 'error');
      }
      onRefresh();
    } catch {
      showToast('Erreur lors de la conversion', 'error');
    } finally {
      setRetryingDocs(new Set());
    }
  };

  return {
    retryingDocs,
    retryConversion,
    retryAllFailed,
  };
}
